import type { QuizQuestionFormat } from "./quiz-question-format";
import type { QuizResponsePayload } from "./quiz-response-payload";

export interface QuizHintRevealAnswer {
	id: string;
	answerText: string;
	role: string;
	isCorrect: boolean;
	displayOrder: number | null;
	/** PAIR_RIGHT / PAIR_LEFT pairing key when present. */
	matchKey?: string | null;
}

/**
 * Mechanical + copy snapshot produced when a hint is spent.
 * Must be deterministic for the same inputs (persist as revealJson; never re-roll).
 */
export interface QuizHintReveal {
	hintText: string | null;
	eliminatedAnswerIds: string[];
	revealedAnswerIds: string[];
	filledBlankValues: string[];
	filledBlankIndexes: number[];
	prefix: string | null;
	lockedTokenAnswerIds: string[];
}

/**
 * In-progress learner draft used to prefer revealing *unsolved* units.
 * Optional — omit / empty behaves like pre-answer (legacy stable picks).
 */
export interface QuizHintLearnerProgress {
	blankValues?: string[];
	selectedAnswerIds?: string[];
	orderedAnswerIds?: string[];
	pairs?: Array<{ leftId: string; rightId: string }>;
	text?: string;
}

export function emptyQuizHintReveal(
	hintText: string | null = null,
): QuizHintReveal {
	return {
		hintText,
		eliminatedAnswerIds: [],
		revealedAnswerIds: [],
		filledBlankValues: [],
		filledBlankIndexes: [],
		prefix: null,
		lockedTokenAnswerIds: [],
	};
}

/** Lexicographic stable slice — never random. */
export function selectStableSlice(ids: string[], count: number): string[] {
	if (count <= 0 || ids.length === 0) {
		return [];
	}
	const sorted = [...ids].sort((a, b) => a.localeCompare(b));
	return sorted.slice(0, Math.min(count, sorted.length));
}

/**
 * Prefer `preferred` ids (stable order preserved), then fill from `fallback`
 * via lexicographic slice until `count`.
 */
export function selectPreferringIds(
	preferred: string[],
	fallback: string[],
	count: number,
): string[] {
	if (count <= 0) {
		return [];
	}
	const out: string[] = [];
	const seen = new Set<string>();
	for (const id of preferred) {
		if (out.length >= count) {
			break;
		}
		if (seen.has(id)) {
			continue;
		}
		seen.add(id);
		out.push(id);
	}
	if (out.length >= count) {
		return out;
	}
	const remainingFallback = fallback.filter((id) => !seen.has(id));
	return [
		...out,
		...selectStableSlice(remainingFallback, count - out.length),
	];
}

export function quizFibHasWordBank(
	formatConfig: Record<string, unknown> | null | undefined,
): boolean {
	return formatConfig?.interaction === "WORD_BANK_SELECT";
}

export function quizHintShouldShowTextBanner(params: {
	format: QuizQuestionFormat;
	formatConfig?: Record<string, unknown> | null;
}): boolean {
	if (params.format === "TYPE_ANSWER") {
		return true;
	}
	if (params.format === "FILL_IN_BLANK") {
		return !quizFibHasWordBank(params.formatConfig);
	}
	return false;
}

export function quizHintLearnerProgressFromResponse(
	response: QuizResponsePayload | null | undefined,
): QuizHintLearnerProgress {
	if (!response) {
		return {};
	}
	switch (response.format) {
		case "FILL_IN_BLANK":
			return { blankValues: response.blankValues };
		case "TYPE_ANSWER":
			return { text: response.text };
		case "MULTIPLE_CHOICE_MULTI":
			return { selectedAnswerIds: response.selectedAnswerIds };
		case "WORD_REORDER":
		case "RANK_ORDER":
			return { orderedAnswerIds: response.orderedAnswerIds };
		case "PAIR_MATCHING":
			return { pairs: response.pairs };
		case "MULTIPLE_CHOICE_SINGLE":
			return {};
		default: {
			const _exhaustive: never = response;
			void _exhaustive;
			return {};
		}
	}
}

function asPayload(value: unknown): Record<string, unknown> | null {
	if (value && typeof value === "object" && !Array.isArray(value)) {
		return value as Record<string, unknown>;
	}
	return null;
}

function normalizeHintText(value: string): string {
	return value.trim().replace(/\s+/g, " ").toLowerCase();
}

function isDistractor(answer: QuizHintRevealAnswer): boolean {
	return (
		answer.role === "DISTRACTOR" ||
		(!answer.isCorrect &&
			answer.role !== "CORRECT" &&
			answer.role !== "BLANK_FILL" &&
			answer.role !== "WORD_BANK_TOKEN" &&
			answer.role !== "PAIR_LEFT" &&
			answer.role !== "PAIR_RIGHT" &&
			answer.role !== "REORDER_TOKEN" &&
			answer.role !== "RANK_ITEM")
	);
}

function isCorrectChoice(answer: QuizHintRevealAnswer): boolean {
	return answer.role === "CORRECT" || answer.isCorrect;
}

function sortByDisplayOrder(
	answers: QuizHintRevealAnswer[],
): QuizHintRevealAnswer[] {
	return [...answers].sort((a, b) => {
		const order = (a.displayOrder ?? 0) - (b.displayOrder ?? 0);
		if (order !== 0) {
			return order;
		}
		return a.id.localeCompare(b.id);
	});
}

function blankIndex(
	blank: QuizHintRevealAnswer,
	blanks: QuizHintRevealAnswer[],
): number {
	if (typeof blank.displayOrder === "number") {
		return blank.displayOrder;
	}
	return blanks.findIndex((item) => item.id === blank.id);
}

function eliminateCount(distractorCount: number): number {
	if (distractorCount <= 0) {
		return 0;
	}
	return distractorCount <= 3 ? 1 : 2;
}

function lockTokenCount(itemCount: number): number {
	if (itemCount <= 0) {
		return 0;
	}
	return itemCount <= 3 ? 1 : 2;
}

function fibFillCount(blankCount: number): number {
	if (blankCount <= 1) {
		return 0;
	}
	return Math.min(2, Math.ceil(blankCount / 3));
}

function blankOrderMatters(
	formatConfig: Record<string, unknown> | null | undefined,
): boolean {
	return formatConfig?.blankOrderMatters !== false;
}

/**
 * Prefer unsolved blanks; never spend the fill budget confirming already-correct slots
 * while unsolved ones remain.
 */
function selectFibMultiFills(params: {
	blanks: QuizHintRevealAnswer[];
	progress: QuizHintLearnerProgress | null | undefined;
	formatConfig: Record<string, unknown> | null | undefined;
	hintPayload: Record<string, unknown> | null;
}): { indexes: number[]; values: string[] } {
	const { blanks, progress, formatConfig, hintPayload } = params;
	const fillCount = fibFillCount(blanks.length);
	const blankValues = progress?.blankValues ?? [];
	const ordered = blankOrderMatters(formatConfig);

	const preferredIndex =
		typeof hintPayload?.index === "number"
			? Math.max(0, Math.floor(hintPayload.index))
			: null;

	if (ordered) {
		const unsolved = blanks.filter((blank) => {
			const index = blankIndex(blank, blanks);
			const actual = normalizeHintText(blankValues[index] ?? "");
			const expected = normalizeHintText(blank.answerText);
			return actual.length === 0 || actual !== expected;
		});
		const pool = unsolved.length > 0 ? unsolved : blanks;
		const orderedPool = [...pool];
		if (
			preferredIndex != null &&
			preferredIndex >= 0 &&
			preferredIndex < blanks.length
		) {
			const preferredBlank = blanks[preferredIndex];
			const inPool = preferredBlank
				? orderedPool.findIndex((b) => b.id === preferredBlank.id)
				: -1;
			if (inPool > 0) {
				const [preferred] = orderedPool.splice(inPool, 1);
				if (preferred) {
					orderedPool.unshift(preferred);
				}
			}
		}
		const selected = orderedPool.slice(0, fillCount);
		return {
			indexes: selected.map((blank) => blankIndex(blank, blanks)),
			values: selected.map((blank) => blank.answerText),
		};
	}

	// Unordered: consume correct fills from the expected multiset, then place
	// remaining expected answers into unsolved slots (empty first, then wrong).
	const satisfiedIndexes = new Set<number>();
	const consumeScratch = new Map<string, number>();
	for (const blank of blanks) {
		const key = normalizeHintText(blank.answerText);
		consumeScratch.set(key, (consumeScratch.get(key) ?? 0) + 1);
	}
	const slotStates = blanks.map((_blank, index) => {
		const raw = blankValues[index] ?? "";
		const actual = normalizeHintText(raw);
		return { index, actual, empty: actual.length === 0 };
	});
	for (const slot of slotStates) {
		if (slot.empty) {
			continue;
		}
		const remaining = consumeScratch.get(slot.actual) ?? 0;
		if (remaining > 0) {
			consumeScratch.set(slot.actual, remaining - 1);
			satisfiedIndexes.add(slot.index);
		}
	}

	const emptyUnsolved = slotStates
		.filter((slot) => slot.empty)
		.map((slot) => slot.index);
	const wrongUnsolved = slotStates
		.filter((slot) => !slot.empty && !satisfiedIndexes.has(slot.index))
		.map((slot) => slot.index);
	const unsolvedIndexes = [...emptyUnsolved, ...wrongUnsolved];
	const targetIndexes =
		unsolvedIndexes.length > 0
			? unsolvedIndexes
			: blanks.map((_, index) => index);

	const remainingExpectedTexts: string[] = [];
	const remainingCounts = new Map<string, number>();
	for (const blank of blanks) {
		const key = normalizeHintText(blank.answerText);
		remainingCounts.set(key, (remainingCounts.get(key) ?? 0) + 1);
	}
	for (const index of satisfiedIndexes) {
		const key = normalizeHintText(blankValues[index] ?? "");
		remainingCounts.set(
			key,
			Math.max(0, (remainingCounts.get(key) ?? 0) - 1),
		);
	}
	for (const blank of blanks) {
		const key = normalizeHintText(blank.answerText);
		const left = remainingCounts.get(key) ?? 0;
		if (left > 0) {
			remainingExpectedTexts.push(blank.answerText);
			remainingCounts.set(key, left - 1);
		}
	}

	const selectedIndexes = targetIndexes.slice(0, fillCount);
	const selectedValues = remainingExpectedTexts.slice(
		0,
		selectedIndexes.length,
	);
	return {
		indexes: selectedIndexes.slice(0, selectedValues.length),
		values: selectedValues,
	};
}

/**
 * Build a deterministic hint reveal for any quiz format.
 * Prefer hintPayload seeds when valid; prefer unsolved learner progress when provided.
 */
export function buildQuizHintReveal(params: {
	format: QuizQuestionFormat;
	hintText: string | null;
	hintPayload?: unknown;
	answers: QuizHintRevealAnswer[];
	formatConfig?: Record<string, unknown> | null;
	/** Current draft — prefer revealing units the learner has not already got right. */
	progress?: QuizHintLearnerProgress | null;
}): QuizHintReveal {
	const base = emptyQuizHintReveal(params.hintText);
	const hintPayload = asPayload(params.hintPayload);
	const progress = params.progress ?? null;

	switch (params.format) {
		case "MULTIPLE_CHOICE_SINGLE": {
			const distractors = params.answers.filter(isDistractor);
			const count = eliminateCount(distractors.length);
			const preferred: string[] = [];
			if (hintPayload?.kind === "eliminate") {
				const answerText =
					typeof hintPayload.answerText === "string"
						? hintPayload.answerText.trim()
						: null;
				if (answerText) {
					const match = distractors.find(
						(answer) => answer.answerText.trim() === answerText,
					);
					if (match) {
						preferred.push(match.id);
					}
				}
			}
			const remaining = distractors
				.map((answer) => answer.id)
				.filter((id) => !preferred.includes(id));
			const eliminatedAnswerIds = [
				...preferred,
				...selectStableSlice(remaining, count - preferred.length),
			].slice(0, count);
			return { ...base, eliminatedAnswerIds };
		}
		case "MULTIPLE_CHOICE_MULTI": {
			const distractors = params.answers.filter(isDistractor);
			const corrects = params.answers.filter(isCorrectChoice);
			const selected = new Set(progress?.selectedAnswerIds ?? []);
			const wrongSelected = distractors
				.filter((answer) => selected.has(answer.id))
				.map((answer) => answer.id)
				.sort((a, b) => a.localeCompare(b));
			const otherDistractors = distractors
				.map((answer) => answer.id)
				.filter((id) => !wrongSelected.includes(id));
			const unrevealedCorrect = corrects
				.filter((answer) => !selected.has(answer.id))
				.map((answer) => answer.id)
				.sort((a, b) => a.localeCompare(b));
			const otherCorrect = corrects.map((answer) => answer.id);
			const eliminatedAnswerIds = selectPreferringIds(
				wrongSelected,
				otherDistractors,
				distractors.length > 0 ? 1 : 0,
			);
			const revealedAnswerIds = selectPreferringIds(
				unrevealedCorrect,
				otherCorrect,
				corrects.length > 0 ? 1 : 0,
			);
			return { ...base, eliminatedAnswerIds, revealedAnswerIds };
		}
		case "FILL_IN_BLANK": {
			const blanks = sortByDisplayOrder(
				params.answers.filter((answer) => answer.role === "BLANK_FILL"),
			);
			if (blanks.length === 0) {
				return base;
			}
			if (blanks.length === 1) {
				const text = blanks[0]?.answerText.trim() ?? "";
				const actual = normalizeHintText(
					progress?.blankValues?.[0] ?? "",
				);
				const expected = normalizeHintText(text);
				if (actual.length > 0 && actual === expected) {
					return base;
				}
				const prefixLength =
					typeof hintPayload?.length === "number"
						? Math.max(1, Math.floor(hintPayload.length))
						: 1;
				const prefix = text ? text.slice(0, prefixLength) : null;
				if (
					prefix &&
					actual.startsWith(normalizeHintText(prefix)) &&
					actual.length >= normalizeHintText(prefix).length
				) {
					return base;
				}
				return {
					...base,
					prefix,
					filledBlankIndexes: [],
					filledBlankValues: [],
				};
			}
			const fills = selectFibMultiFills({
				blanks,
				progress,
				formatConfig: params.formatConfig,
				hintPayload,
			});
			return {
				...base,
				filledBlankIndexes: fills.indexes,
				filledBlankValues: fills.values,
				prefix: null,
			};
		}
		case "TYPE_ANSWER": {
			const correct = params.answers.find(isCorrectChoice);
			const text = correct?.answerText.trim() ?? "";
			const actual = normalizeHintText(progress?.text ?? "");
			const expected = normalizeHintText(text);
			if (actual.length > 0 && actual === expected) {
				return base;
			}
			const prefixLength =
				typeof hintPayload?.length === "number"
					? Math.max(1, Math.floor(hintPayload.length))
					: 1;
			const prefix = text ? text.slice(0, prefixLength) : null;
			if (
				prefix &&
				actual.startsWith(normalizeHintText(prefix)) &&
				actual.length >= normalizeHintText(prefix).length
			) {
				return base;
			}
			return {
				...base,
				prefix,
			};
		}
		case "WORD_REORDER":
		case "RANK_ORDER": {
			const roleFiltered = sortByDisplayOrder(
				params.answers.filter(
					(answer) =>
						answer.role === "REORDER_TOKEN" ||
						answer.role === "RANK_ITEM",
				),
			);
			const tokens =
				roleFiltered.length > 0
					? roleFiltered
					: sortByDisplayOrder(params.answers);
			const orderedIds = progress?.orderedAnswerIds ?? [];
			const unsolved = tokens.filter((token) => {
				const correctSlot = blankIndex(token, tokens);
				return orderedIds[correctSlot] !== token.id;
			});
			const pool = unsolved.length > 0 ? unsolved : tokens;

			if (
				params.format === "RANK_ORDER" &&
				hintPayload?.kind === "reveal_endpoints" &&
				tokens.length >= 2
			) {
				const first = tokens[0];
				const last = tokens[tokens.length - 1];
				const endpointIds = [first?.id, last?.id].filter(
					(id): id is string => Boolean(id),
				);
				const unsolvedEndpoints = endpointIds.filter((id) =>
					unsolved.some((token) => token.id === id),
				);
				const count = lockTokenCount(tokens.length);
				const lockedTokenAnswerIds = selectPreferringIds(
					unsolvedEndpoints.length > 0
						? unsolvedEndpoints
						: endpointIds,
					pool.map((token) => token.id),
					count,
				);
				return { ...base, lockedTokenAnswerIds };
			}

			const count = lockTokenCount(tokens.length);
			const preferredIndex =
				typeof hintPayload?.index === "number"
					? Math.max(0, Math.floor(hintPayload.index))
					: null;
			const preferredIds: string[] = [];
			if (
				preferredIndex != null &&
				tokens[preferredIndex] &&
				pool.some((token) => token.id === tokens[preferredIndex].id)
			) {
				preferredIds.push(tokens[preferredIndex].id);
			}
			const lockedTokenAnswerIds = selectPreferringIds(
				[...preferredIds, ...pool.map((token) => token.id)],
				tokens.map((token) => token.id),
				count,
			);
			return { ...base, lockedTokenAnswerIds };
		}
		case "PAIR_MATCHING": {
			const left = sortByDisplayOrder(
				params.answers.filter((answer) => answer.role === "PAIR_LEFT"),
			);
			const rights = params.answers.filter(
				(answer) => answer.role === "PAIR_RIGHT",
			);
			const pairs = progress?.pairs ?? [];
			const unsolvedLeft = left.filter((item) => {
				const right = rights.find(
					(candidate) =>
						candidate.matchKey != null &&
						candidate.matchKey === item.matchKey,
				);
				if (!right) {
					return true;
				}
				const matched = pairs.find((pair) => pair.leftId === item.id);
				return matched?.rightId !== right.id;
			});
			const pool = unsolvedLeft.length > 0 ? unsolvedLeft : left;
			const count = lockTokenCount(left.length);
			const preferredIndex =
				typeof hintPayload?.index === "number"
					? Math.max(0, Math.floor(hintPayload.index))
					: null;
			const preferredIds: string[] = [];
			if (
				preferredIndex != null &&
				left[preferredIndex] &&
				pool.some((item) => item.id === left[preferredIndex].id)
			) {
				preferredIds.push(left[preferredIndex].id);
			}
			const revealedAnswerIds = selectPreferringIds(
				[...preferredIds, ...pool.map((item) => item.id)],
				left.map((item) => item.id),
				count,
			);
			return {
				...base,
				revealedAnswerIds,
			};
		}
		default: {
			const _exhaustive: never = params.format;
			void _exhaustive;
			return base;
		}
	}
}
